import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {format} from "date-fns";
import {useTheme} from "../../../../../connectui/theme/ThemeProvider";
import {buildStyles} from "../../../../../connectui/component/buildStyles";
import Container from "../../../../../connectui/component/layout/Container";
import FlexContainer from "../../../../../connectui/component/layout/FlexContainer";
import Text from "../../../../../connectui/component/display/Text";
import Tooltip from "../../../../../connectui/component/display/Tooltip";
import SvgIcon from "../../../../../connectui/component/display/SvgIcon";
import ChipIconText from "connectui/component/display/chip/ChipIconText";
import ICONS from "connectui/icons/iconMap";
import {formatCurrency} from "utilities/currencyUtil";
import BookingStatusField from "ui/component/field/fields/BookingStatusField";
import ButtonIcon from "connectui/component/input/button/ButtonIcon";
import ContextMenu from "connectui/component/input/context/ContextMenu";
import BookingChannelIcon from "./BookingChannelIcon";
import {getHouses} from "../../../../../redux/actions/listingsActions";
import {useTranslation} from "react-i18next";
import BookingTags from "./BookingTags";
import PaymentStatus from "./PaymentStatus";

const useStyles = (theme) =>
  buildStyles({
    cardContainer: {
      width: "100%",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      borderBottom: `1px solid ${theme.colors.border}`,
    },
    imageWrapper: {
      width: "100%",
      position: "relative",
      overflow: "hidden",
      backgroundColor: theme.colors.gray200,
      borderTopLeftRadius: `${theme.radius.xxl}px`,
      borderTopRightRadius: `${theme.radius.xxl}px`,
    },
    imageContainer: {
      width: "100%",
      height: "100%",
      backgroundSize: "cover",
      backgroundPosition: "center",
      transition: "opacity 0.3s ease-in-out",
    },
    statusChip: {
      position: "absolute",
      top: theme.spacing(2),
      left: 0,
      right: 0,
      padding: theme.spacing(0, 2),
    },
    bookingTags: {
      position: "absolute",
      bottom: theme.spacing(2),
      left: 0,
      right: 0,
      padding: theme.spacing(0, 2),
    },
    content: {
      padding: theme.spacing(3),
      width: "100%",
      overflow: "hidden",
    },
    houseName: {
      overflow: "hidden",
      cursor: "pointer",
      textOverflow: "ellipsis",
    },
    listingGroupName: {
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    iconBtn: {
      borderRadius: theme.spacing(4),
      backgroundColor: theme.colors.gray100,
      cursor: "pointer",
      padding: theme.spacing(1),
      border: "none",
      "&:hover": {backgroundColor: theme.colors.gray200},
    },
  });

const formatTime = (time) => {
  if (!time) return "";
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
};

const paymentStatusColors = {
  scheduled: "gray500",
  requires_action: "warning500",
  overpaid: "error500",
  paid: "success500",
  partial_paid: "primary500",
  unpaid: "error500",
  refunded: "error500",
  cancelled: "error500",
  failed: "error500",
  on_hold: "gray500",
};

const ListingInfoCard = ({booking, openListingDetails}) => {
  const {theme} = useTheme();
  const classes = useStyles(theme);
  const dispatch = useDispatch();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const {t} = useTranslation();
  const houses = useSelector((state) => state.defaultReducer.house_data_dict);
  const groups = useSelector(
    (state) => state.defaultReducer.listing_groups_dict,
  );
  const current_user = useSelector(
    (state) => state.defaultReducer.current_user,
  );
  let house = houses[booking.listing_id];
  const paymentStatusColor =
    paymentStatusColors[booking.payment?.status] || "gray500";

  React.useEffect(() => {
    if (house === undefined) {
      dispatch(
        getHouses(
          `listing_id=${booking.listing_id}&enso_key=${current_user}`,
          true,
        ),
      );
    }
  }, [booking.listing_id, current_user, dispatch, house]);

  const group = groups[house?.group_id];
  let houseName = house?.nickname || house?.name || "";
  let picture = house?.picture || "";
  let price =
    booking.total_paid ||
    booking.payment?.total_guest_paid ||
    booking.payment?.host_payout_amount ||
    0;
  let bookingStatus = booking?.status || "inquiry";

  const {checkinDate, checkoutDate} = React.useMemo(() => {
    const {
      checkin_date,
      checkout_date,
      checkin_raw,
      checkout_raw,
      checkin_time,
      checkout_time,
    } = booking;
    let checkinDate,
      checkoutDate = "";
    if (!!checkin_date || !!checkout_date) {
      checkinDate = !!checkin_date
        ? `${format(new Date(booking.checkin_date), "MMM d, yyyy")}${checkin_time ? `, ${formatTime(checkin_time)}` : ""}`
        : "";
      checkoutDate = !!checkout_date
        ? `${format(new Date(booking.checkout_date), "MMM d, yyyy")}${checkout_time ? `, ${formatTime(checkout_time)}` : ""}`
        : "";
    } else if (!!checkin_raw || !!checkout_raw) {
      checkinDate = !!checkin_raw
        ? `${format(checkin_raw, "MMM d, yyyy")}${checkin_time ? `, ${formatTime(checkin_time)}` : ""}`
        : "";
      checkoutDate = !!checkout_raw
        ? `${format(checkout_raw, "MMM d, yyyy")}${checkout_time ? `, ${formatTime(checkout_time)}` : ""}`
        : "";
    }
    return {checkinDate, checkoutDate};
  }, [booking]);

  const guests = (booking.num_adult || 0) + (booking.num_child || 0);
  const numAdults = booking.num_adult || 0;
  const numChildren = booking.num_child || 0;
  const numPets = booking.num_pets || 0;

  const guestDetailsTooltip = [
    `${numAdults} ${numAdults === 1 ? "Adult" : "Adults"}`,
    `${numChildren} ${numChildren === 1 ? "Child" : "Children"}`,
    `${numPets} ${numPets === 1 ? "Pet" : "Pets"}`,
  ].join("\n");

  useEffect(() => {
    if (picture) {
      const img = new Image();
      img.src = picture;
      img.onload = () => setImageLoaded(true);
    }
  }, [picture]);

  return (
    <Container className={classes.cardContainer}>
      <ContextMenu
        menuItems={[
          {
            label: "Listing Information",
            icon: ICONS.info,
            onClick: () => {
              setAnchorEl(null);
              openListingDetails(0);
            },
          },
        ]}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
      />
      <Container
        className={classes.imageWrapper}
        sx={{height: booking.tags?.length > 0 ? "130px" : "100px"}}
      >
        <Container
          className={classes.imageContainer}
          sx={{
            backgroundImage: imageLoaded ? `url(${picture})` : "none",
            opacity: imageLoaded ? 1 : 0,
          }}
          skeleton={{enabled: !imageLoaded, width: "100%", height: "100px"}}
        />
        <FlexContainer
          className={classes.statusChip}
          gap={theme.spacing(1)}
          alignItems="center"
        >
          <Container sx={{flex: 1}}>
            <Tooltip
              interactive
              tooltipContent={
                !!booking.integration_booking_id && (
                  <FlexContainer flexDirection="column" gap={theme.spacing(1)}>
                    <Text variant="textXS" weight="semibold" color="white">
                      {t("inbox.reservation-details.pms-booking-id")}
                    </Text>
                    <Text variant="textXS" color="white">
                      {booking.integration_booking_id}
                    </Text>
                  </FlexContainer>
                )
              }
              leaveDelay={150}
              colors={{bg: "gray900", text: "white"}}
            >
              <BookingChannelIcon channel={booking.channel} />
            </Tooltip>
          </Container>
          <BookingStatusField fieldData={{val: bookingStatus}} type="display" />
          <ButtonIcon
            icon={
              <SvgIcon
                component={ICONS["more-vertical"]}
                sx={{
                  width: "12px",
                  height: "12px",
                  color: theme.colors.textSecondary,
                }}
              />
            }
            className={classes.iconBtn}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          />
        </FlexContainer>

        <FlexContainer
          className={classes.bookingTags}
          gap={theme.spacing(1)}
          alignItems="center"
        >
          <BookingTags bookingTags={booking.tags} />
        </FlexContainer>
      </Container>

      <FlexContainer
        className={classes.content}
        flexDirection="column"
        gap={theme.spacing(2.5)}
      >
        <FlexContainer
          gap={theme.spacing(2)}
          justifyContent={"space-between"}
          alignItems={"flex-start"}
          sx={{width: "100%"}}
        >
          <FlexContainer
            flexDirection="column"
            sx={{flex: 1, overflow: "hidden"}}
          >
            <Tooltip
              tooltipContent={
                <Text
                  variant="textXS"
                  color="white"
                  skeleton={{
                    enabled: !houseName,
                    width: "200px",
                    height: "24px",
                  }}
                >
                  {houseName}
                </Text>
              }
              enterDelay={500}
              leaveDelay={0}
              colors={{bg: "gray900", text: "white"}}
              sx={{
                trigger: {
                  width: "100%",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  cursor: "pointer",
                },
                tooltip: {padding: theme.spacing(1.5)},
              }}
            >
              <Text
                className={classes.houseName}
                skeleton={{enabled: !houseName, width: "100%", height: "24px"}}
                sx={{cursor: "pointer"}}
              >
                {houseName}
              </Text>
            </Tooltip>
            {group?.name && (
              <Text
                className={classes.listingGroupName}
                variant="textXS"
                color="textSecondary"
              >
                {group.name}
              </Text>
            )}
          </FlexContainer>
          <FlexContainer
            justifyContent={"space-between"}
            alignItems={"center"}
            sx={{minWidth: "fit-content"}}
          >
            {!!booking.payment?.status && (
              <PaymentStatus
                status={booking.payment.status}
                baseColor={paymentStatusColor}
              />
            )}
            <Text>{formatCurrency(price, booking.payment?.currency)}</Text>
          </FlexContainer>
        </FlexContainer>

        <FlexContainer
          justifyContent={"space-between"}
          alignItems={"flex-end"}
          sx={{width: "100%"}}
        >
          <FlexContainer
            flexDirection="column"
            gap={theme.spacing(0.5)}
            justifyContent="center"
          >
            <FlexContainer gap={theme.spacing(1)} alignItems={"center"}>
              <SvgIcon
                component={ICONS["check-in"]}
                sx={{
                  color: theme.colors.textSecondary,
                  width: "12px",
                  height: "12px",
                }}
              />
              <Tooltip
                tooltipContent={
                  <Text variant="textXS" color="white">
                    {t(
                      "inbox.guest-details.reservations.checkin-checkout-timezone-tooltip",
                    )}
                  </Text>
                }
                enterDelay={500}
                leaveDelay={0}
                colors={{bg: "gray900", text: "white"}}
                sx={{
                  tooltip: {padding: theme.spacing(1.5)},
                  trigger: {cursor: "pointer"},
                }}
              >
                <Text
                  variant="textXS"
                  weight="medium"
                  color="textSecondary"
                  skeleton={{
                    enabled: !checkinDate,
                    width: "120px",
                    height: "18px",
                  }}
                >
                  {checkinDate}
                </Text>
              </Tooltip>
            </FlexContainer>
            <FlexContainer gap={theme.spacing(1)} alignItems={"center"}>
              <SvgIcon
                component={ICONS["check-out"]}
                sx={{
                  color: theme.colors.textSecondary,
                  width: "12px",
                  height: "12px",
                }}
              />
              <Tooltip
                tooltipContent={
                  <Text variant="textXS" color="white">
                    {t(
                      "inbox.guest-details.reservations.checkin-checkout-timezone-tooltip",
                    )}
                  </Text>
                }
                enterDelay={500}
                leaveDelay={0}
                colors={{bg: "gray900", text: "white"}}
                sx={{
                  tooltip: {padding: theme.spacing(1.5)},
                  trigger: {cursor: "pointer"},
                }}
              >
                <Text
                  variant="textXS"
                  weight="medium"
                  color="textSecondary"
                  skeleton={{
                    enabled: !checkoutDate,
                    width: "120px",
                    height: "18px",
                  }}
                >
                  {checkoutDate}
                </Text>
              </Tooltip>
            </FlexContainer>
          </FlexContainer>
          {(!bookingStatus.includes("inquiry") ||
            numAdults + numChildren + numPets > 0) && (
            <Tooltip
              tooltipContent={
                <FlexContainer flexDirection="column">
                  {guestDetailsTooltip.split("\n").map((line, index) => (
                    <Text key={index} variant="textXS" color="white">
                      {line}
                    </Text>
                  ))}
                </FlexContainer>
              }
              enterDelay={500}
              leaveDelay={0}
              colors={{bg: "gray900", text: "white"}}
              sx={{
                tooltip: {padding: theme.spacing(1.5)},
                trigger: {cursor: "pointer"},
              }}
            >
              <ChipIconText
                text={`${guests} Guest${guests === 1 ? "" : "s"}`}
                typographyVariant="textXS"
                weight="medium"
                textColor="gray700"
                icon={
                  <SvgIcon
                    component={guests === 1 ? ICONS.user : ICONS.users}
                    sx={{
                      color: theme.colors.gray700,
                      width: "13px",
                      height: "13px",
                    }}
                  />
                }
                sx={{
                  backgroundColor: theme.colors.gray100,
                  color: theme.colors.gray700,
                  "&:hover": {backgroundColor: theme.colors.gray100},
                  "& *": {cursor: "pointer"},
                }}
                skeleton={{
                  enabled: !houseName,
                  width: "80px",
                  height: "20px",
                }}
              />
            </Tooltip>
          )}
        </FlexContainer>
      </FlexContainer>
    </Container>
  );
};

export default ListingInfoCard;
